import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../lib/format';
import { useUser } from '../store/useAuthStore';
import { apiClient } from '../lib/api';
// Removed import - using local formatCurrency function instead
import { InvoiceBreakdown } from '../components/InvoiceBreakdown';
import { formatShortDate, formatDateTime } from '../utils/dateUtils';
import { 
  DollarSign, 
  Calendar, 
  Search, 
  CheckCircle, 
  Clock,
  AlertCircle,
  Eye,
  Filter,
  Download,
  TrendingUp,
  FileText,
  Send
} from 'lucide-react';

interface Payout {
  _id: string;
  recipientId: string;
  createdById: string;
  startDate: string;
  endDate: string;
  amount: number;
  grossAmount?: number;
  clinicSiteFeeCents?: number;
  status: 'pending' | 'sent' | 'confirmed' | 'cancelled';
  wireNumber?: string;
  notesForRecipient?: string;
  sentAt?: string;
  confirmedAt?: string;
  createdAt: string;
  updatedAt: string;
  // Flattened user info from backend
  createdByName?: string;
  createdByEmail?: string;
}

// Roles that receive payouts directly. 'partner' is a payout-only account
// (lead source / departed rep on a commission override) whose earnings arrive
// entirely through the override ledger — this page is their whole frontend.
const RECIPIENT_ROLES = ['distributor', 'supervisor', 'partner'];

// Local utility function

export const RecipientPayouts: React.FC = () => {
  const user = useUser();
  
  // State management
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'sent' | 'confirmed' | 'cancelled'>('all');
  
  // Modal states
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState<Payout | null>(null);
  const [payoutInvoiceBreakdown, setPayoutInvoiceBreakdown] = useState<any>(null);
  const [loadingInvoiceBreakdown, setLoadingInvoiceBreakdown] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmingPayout, setConfirmingPayout] = useState<Payout | null>(null);

  // Load data on component mount
  useEffect(() => {
    if (RECIPIENT_ROLES.includes(user?.role as string)) {
      loadPayouts();
    }
  }, [user]);

  const loadPayouts = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getPayouts();
      if (response.success && response.data) {
        setPayouts(response.data);
      } else {
        setError(response.error || 'Failed to load payouts');
      }
    } catch (error: any) {
      setError('Failed to load payouts');
      console.error('Error loading payouts:', error);
    } finally {
      setLoading(false);
    }
  };

  const openConfirmModal = (payout: Payout) => {
    setConfirmingPayout(payout);
    setShowConfirmModal(true);
  };

  const confirmReceipt = async (payoutId: string) => {
    try {
      const response = await apiClient.confirmPayout(payoutId);
      if (response.success) {
        await loadPayouts();
        setShowConfirmModal(false);
        setConfirmingPayout(null);
        console.log('Payout receipt confirmed successfully');
      } else {
        console.error('Failed to confirm payout receipt:', response.error);
      }
    } catch (error: any) {
      console.error('Error confirming payout receipt:', error);
    }
  };

  const loadPayoutInvoiceBreakdown = async (payoutId: string) => {
    setLoadingInvoiceBreakdown(true);
    setPayoutInvoiceBreakdown(null);
    
    try {
      const response = await apiClient.getPayoutInvoiceBreakdown(payoutId);
      if (response.success) {
        setPayoutInvoiceBreakdown(response.data);
      } else {
        console.error('Failed to load invoice breakdown:', response.error);
      }
    } catch (error: any) {
      console.error('Error loading invoice breakdown:', error);
    } finally {
      setLoadingInvoiceBreakdown(false);
    }
  };

  // Filter payouts based on search and status
  const filteredPayouts = payouts.filter(payout => {
    const matchesSearch = searchTerm === '' || 
      payout.wireNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payout.notesForRecipient?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      formatShortDate(payout.startDate).includes(searchTerm) ||
      formatShortDate(payout.endDate).includes(searchTerm);
    
    const matchesStatus = statusFilter === 'all' || payout.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="w-3 h-3 mr-1" />
          Pending
        </span>;
      case 'sent':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <Send className="w-3 h-3 mr-1" />
          Sent
        </span>;
      case 'confirmed':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          Confirmed
        </span>;
      case 'cancelled':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertCircle className="w-3 h-3 mr-1" />
          Cancelled
        </span>;
      default:
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          {status}
        </span>;
    }
  };

  // Calculate summary statistics
  const summaryStats = {
    total: payouts.length,
    pending: payouts.filter(p => p.status === 'pending').length,
    confirmed: payouts.filter(p => p.status === 'confirmed').length,
    totalAmount: payouts.reduce((sum, p) => sum + p.amount, 0),
    confirmedAmount: payouts.filter(p => p.status === 'confirmed').reduce((sum, p) => sum + p.amount, 0),
    pendingAmount: payouts.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0)
  };

  // Get recent payouts (last 3 months)
  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
  const recentPayouts = payouts.filter(p => new Date(p.createdAt) >= threeMonthsAgo);

  if (!user || !RECIPIENT_ROLES.includes(user.role)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500">This page is only available to payout recipients.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-grey-50 to-emerald-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Your Payouts</h1>
          <p className="text-gray-600 mt-2">Track payments received for your distribution services</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <DollarSign className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Received</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(summaryStats.confirmedAmount)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-yellow-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(summaryStats.pendingAmount)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <FileText className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Payouts</p>
                <p className="text-2xl font-bold text-gray-900">{summaryStats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Last 3 Months</p>
                <p className="text-2xl font-bold text-gray-900">{recentPayouts.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        {summaryStats.pending > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
            <div className="flex items-center">
              <Clock className="h-5 w-5 text-yellow-600" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  You have {summaryStats.pending} pending payout{summaryStats.pending > 1 ? 's' : ''}
                </h3>
                <p className="text-sm text-yellow-700 mt-1">
                  Total pending amount: {formatCurrency(summaryStats.pendingAmount)}. These will be processed soon.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex-1 flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search payouts, wire numbers, dates..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-gray-400" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              {payouts.length > 0 && (
                <button
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <p className="ml-2 text-red-700">{error}</p>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-400 hover:text-red-600"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Payouts Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">Loading your payouts...</p>
            </div>
          ) : filteredPayouts.length === 0 ? (
            <div className="p-8 text-center">
              <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No payouts found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {payouts.length === 0 ? 'You haven\'t received any payouts yet.' : 'Try adjusting your search or filter criteria.'}
              </p>
            </div>
          ) : (
            <div className="data-table-container">
              <div className="data-table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Wire Number</th>
                      <th>Processed By</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayouts.map((payout) => (
                      <tr key={payout._id}>
                        <td className="cell-primary">
                          <div className="cell-stack">
                            <span className="cell-stack-value">
                              {formatShortDate(payout.startDate)} - {formatShortDate(payout.endDate)}
                            </span>
                            <span className="cell-stack-label">
                              {Math.ceil((new Date(payout.endDate).getTime() - new Date(payout.startDate).getTime()) / (1000 * 60 * 60 * 24))} days
                            </span>
                          </div>
                        </td>
                        <td className="cell-mono text-lg font-bold">
                          {formatCurrency(payout.amount)}
                          {!!payout.clinicSiteFeeCents && <div className="text-xs font-normal text-gray-600">Gross {formatCurrency(payout.grossAmount || 0)} · clinic domain fee −{formatCurrency(payout.clinicSiteFeeCents / 100)}</div>}
                        </td>
                        <td className="cell-secondary">
                          {getStatusBadge(payout.status)}
                        </td>
                        <td className="cell-mono">
                          {payout.wireNumber || '-'}
                        </td>
                        <td className="cell-primary">
                          <div className="cell-stack">
                            <span className="cell-stack-value">{payout.createdByName || 'Unknown User'}</span>
                            <span className="cell-stack-label">{payout.createdByEmail || 'No email'}</span>
                          </div>
                        </td>
                        <td className="cell-secondary">
                          <div className="cell-stack">
                            <span className="cell-stack-value">{formatShortDate(payout.createdAt)}</span>
                            {payout.confirmedAt && (
                              <span className="cell-stack-label text-green-600">
                                Confirmed: {formatShortDate(payout.confirmedAt)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="cell-actions">
                          <div className="table-actions">
                            <button
                              onClick={() => {
                                setSelectedPayout(payout);
                                setShowDetailsModal(true);
                                loadPayoutInvoiceBreakdown(payout._id);
                              }}
                              className="table-action-btn table-action-btn-primary"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            {(payout.status === 'sent' || payout.status === 'pending') && (
                              <button
                                onClick={() => openConfirmModal(payout)}
                                className="table-action-btn table-action-btn-success"
                                title="Confirm Receipt"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Payout Details Modal */}
      {showDetailsModal && selectedPayout && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Payout Details</h2>
            </div>
            
            <div className="px-6 py-4 space-y-6">
              {/* Status and Amount */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Status</h3>
                  {getStatusBadge(selectedPayout.status)}
                  {selectedPayout.status === 'pending' && (
                    <p className="text-xs text-yellow-600 mt-1">Payment is being processed - you can confirm if you received payment</p>
                  )}
                  {selectedPayout.status === 'sent' && (
                    <p className="text-xs text-blue-600 mt-1">Payment has been sent - please confirm receipt</p>
                  )}
                  {selectedPayout.status === 'confirmed' && selectedPayout.confirmedAt && (
                    <p className="text-xs text-green-600 mt-1">
                      Confirmed on {formatShortDate(selectedPayout.confirmedAt)}
                    </p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Amount</h3>
                  <p className="text-3xl font-bold text-gray-900">{formatCurrency(selectedPayout.amount)}</p>
                </div>
              </div>

              {/* Period Information */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Revenue Period</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">
                        {formatShortDate(selectedPayout.startDate)} - {formatShortDate(selectedPayout.endDate)}
                      </p>
                      <p className="text-sm text-gray-500">
                        {Math.ceil((new Date(selectedPayout.endDate).getTime() - new Date(selectedPayout.startDate).getTime()) / (1000 * 60 * 60 * 24))} days
                      </p>
                    </div>
                    <Calendar className="h-8 w-8 text-gray-400" />
                  </div>
                </div>
              </div>

              {/* Wire Transfer Information */}
              {selectedPayout.wireNumber && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Wire Transfer</h3>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="font-mono text-lg text-blue-900">{selectedPayout.wireNumber}</p>
                    <p className="text-sm text-blue-600 mt-1">Reference this number for your records</p>
                  </div>
                </div>
              )}

              {/* Notes from Admin */}
              {selectedPayout.notesForRecipient && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Notes</h3>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-gray-900">{selectedPayout.notesForRecipient}</p>
                  </div>
                </div>
              )}

              {/* Processing Information */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Processing Information</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Processed by:</span>
                    <span className="text-gray-900">
                      {selectedPayout.createdByName || 'Unknown User'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Created:</span>
                    <span className="text-gray-900">{formatDateTime(selectedPayout.createdAt)}</span>
                  </div>
                  {selectedPayout.confirmedAt && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Confirmed:</span>
                      <span className="text-green-700">{formatDateTime(selectedPayout.confirmedAt)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Instructions Reminder */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-yellow-800">Payment Instructions</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      Make sure your payment instructions are up to date in your settings. 
                      This helps ensure smooth processing of future payouts.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Invoice Breakdown Section */}
            <div className="px-6 py-4 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Breakdown</h3>
              {loadingInvoiceBreakdown ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="text-gray-500 mt-2">Loading invoice details...</p>
                </div>
              ) : payoutInvoiceBreakdown?.invoiceDetails ? (
                <InvoiceBreakdown 
                  invoices={payoutInvoiceBreakdown.invoiceDetails}
                  title={`Your Invoices for ${formatShortDate(selectedPayout.startDate)} - ${formatShortDate(selectedPayout.endDate)}`}
                  showRevenue={true}
                  showProductCost={false}
                />
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No invoice details available</p>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
              <div>
                {(selectedPayout.status === 'sent' || selectedPayout.status === 'pending') && (
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      openConfirmModal(selectedPayout);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Confirm Receipt
                  </button>
                )}
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && confirmingPayout && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Confirm Payment Receipt</h2>
            </div>
            
            <div className="px-6 py-4">
              <div className="mb-4">
                <p className="text-gray-700 mb-2">
                  <strong>{confirmingPayout.createdByName || 'Administrator'}</strong> {confirmingPayout.status === 'pending' ? 'did not mark this payout as sent' : 'has marked this payout as sent'}.
                </p>
                
                {confirmingPayout.status === 'pending' && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                    <p className="text-yellow-800 text-sm">
                      ⚠️ By confirming, you are stating that you received payment for this period, even though an Administrator did not mark it as sent.
                    </p>
                  </div>
                )}
                
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-semibold">{formatCurrency(confirmingPayout.amount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Period:</span>
                    <span className="text-sm">
                      {formatShortDate(confirmingPayout.startDate)} - {formatShortDate(confirmingPayout.endDate)}
                    </span>
                  </div>
                  {confirmingPayout.wireNumber && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Wire #:</span>
                      <span className="text-sm font-mono">{confirmingPayout.wireNumber}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to confirm receipt of this payment?
              </p>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmingPayout(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => confirmReceipt(confirmingPayout._id)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                Yes, Confirm Receipt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
